import { forwardRef } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { theme } from '../../theme';
import { Spinner } from '../Spinner';
import type { LoadingPlaceholderProps, SkeletonShape, SkeletonAnimation } from './LoadingPlaceholder.types';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

interface SkeletonStyleProps {
  $width: number | string;
  $height: number | string;
  $shape: SkeletonShape;
  $animation: SkeletonAnimation;
}

const getBorderRadius = (shape: SkeletonShape): string => {
  switch (shape) {
    case 'circle':
      return '50%';
    case 'rounded':
      return '6px';
    case 'rectangle':
    default:
      return '0';
  }
};

const getWidth = (width: number | string): string => {
  return typeof width === 'number' ? `${width}px` : width;
};

const getHeight = (height: number | string): string => {
  return typeof height === 'number' ? `${height}px` : height;
};

const SkeletonItem = styled.div<SkeletonStyleProps>`
  width: ${({ $width }) => getWidth($width)};
  height: ${({ $height }) => getHeight($height)};
  border-radius: ${({ $shape }) => getBorderRadius($shape)};
  background: ${({ $animation }) =>
    $animation === 'shimmer'
      ? 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'
      : '#f0f0f0'};
  background-size: ${({ $animation }) => ($animation === 'shimmer' ? '200% 100%' : 'auto')};
  animation: ${({ $animation }) => ($animation === 'shimmer' ? shimmer : pulse)} 1.5s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: #f0f0f0;
  }
`;

interface ContainerStyleProps {
  $gap: number | string;
}

const Container = styled.div<ContainerStyleProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => (typeof $gap === 'number' ? `${$gap}px` : $gap)};
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
`;

const LoadingText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily};
`;

/**
 * LoadingPlaceholder - A versatile loading indicator component
 *
 * Displays either skeleton placeholders with shimmer/pulse animation
 * or a spinner with optional loading text.
 *
 * @example
 * ```tsx
 * // Skeleton placeholder for a form field
 * <LoadingPlaceholder height={42} shape="rounded" />
 *
 * // Multiple skeleton items
 * <LoadingPlaceholder count={3} height={20} gap={8} />
 *
 * // Spinner with text
 * <LoadingPlaceholder variant="spinner" text="Loading..." />
 *
 * // Circle skeleton (avatar placeholder)
 * <LoadingPlaceholder width={48} height={48} shape="circle" />
 * ```
 */
export const LoadingPlaceholder = forwardRef<HTMLDivElement, LoadingPlaceholderProps>(
  (
    {
      variant = 'skeleton',
      count = 1,
      height = 42,
      width = '100%',
      shape = 'rounded',
      gap = 8,
      text,
      animation = 'shimmer',
      className,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    if (variant === 'spinner') {
      return (
        <SpinnerContainer
          ref={ref}
          className={className}
          data-testid={dataTestId}
          data-id={dataId}
        >
          <Spinner size="sm" />
          {text && <LoadingText>{text}</LoadingText>}
        </SpinnerContainer>
      );
    }

    if (count === 1) {
      return (
        <SkeletonItem
          ref={ref}
          $width={width}
          $height={height}
          $shape={shape}
          $animation={animation}
          className={className}
          data-testid={dataTestId}
          data-id={dataId}
          aria-busy="true"
          aria-label="Loading"
        />
      );
    }

    return (
      <Container
        ref={ref}
        $gap={gap}
        className={className}
        data-testid={dataTestId}
        data-id={dataId}
        aria-busy="true"
        aria-label="Loading"
      >
        {Array.from({ length: count }, (_, index) => (
          <SkeletonItem
            key={index}
            $width={width}
            $height={height}
            $shape={shape}
            $animation={animation}
          />
        ))}
      </Container>
    );
  }
);

LoadingPlaceholder.displayName = 'LoadingPlaceholder';

export default LoadingPlaceholder;
